/* components/patterns/integrations/RightsChecklist.jsx — что подключённая учётная
   система даёт аналитике. Общий для Настроек → Интеграции (BusinessIntegrationsTab)
   и мастера онбординга. Чисто презентационный: данные и обработку ошибок
   перепроверки держит вызывающий.

   ДВЕ ФОРМЫ ОТВЕТА, РАЗЛИЧАЕТ ПОЛЕ `source` (GET/POST /api/integrations/permissions/).
   Причина развилки — устройство систем, а не оформление:

   * source='rights' (iiko/Syrve) — у сервера есть API-пользователь, и вопрос
     «что доступно» решается ЕГО правами. Выдача прежняя и заморожена: {checked_at,
     capabilities:[{key, tag, name, area, also, optional, status granted|denied|error}],
     by_report{blocked,degraded}, any_denied}. Прав не хватает → это ЧИНИТСЯ в iiko,
     поэтому есть кнопка перепроверки.
   * source='capabilities' (r_keeper) — сессии iiko и прав как понятия нет вовсе,
     подключение читает базу кассы напрямую. Что доступно, определяется тем, какие
     данные в системе ЕСТЬ: {areas:[{key, name, area, status supported|unsupported}]},
     capabilities:[] и any_denied:false. Кнопки перепроверки здесь НЕТ — перепроверять
     нечего: отсутствие себестоимости в r_keeper не отменить настройкой.

   Показать это надо ДО первого похода в Аналитику: пустая вкладка без объяснения
   читается как поломка Ресториума, а не как свойство кассы. Поэтому пустой список
   (data=null / нет подключения / ещё не проверялось) — единственный случай, когда
   блок не рендерится вовсе. Стили — styles/integrations-shared.css (.rights-check*). */

import { Pill } from '../../ui/Badge.jsx';
import { Button } from '../../ui/Button.jsx';
import { Icon } from '../../ui/Icon.jsx';

  // Статус права API-пользователя по области аналитики.
  const CAP_STATUS = {
    granted: { tone: 'ok', label: 'Доступно', icon: 'check-circle' },
    denied: { tone: 'bad', label: 'Нет прав', icon: 'x-circle' },
    error: { tone: 'warn', label: 'Не удалось', icon: 'alert-triangle' },
    unknown: { tone: 'neutral', label: 'Не проверялось', icon: 'minus' },
  };

  // Статус области данных (source='capabilities'). Намеренно НЕ красный: «в системе
  // таких данных нет» — не отказ и не поломка, чинить нечего. Красный тон толкал бы
  // ресторатора искать неисправность там, где её нет.
  const AREA_STATUS = {
    supported: { tone: 'ok', label: 'Есть', icon: 'check-circle' },
    unsupported: { tone: 'neutral', label: 'Нет в системе', icon: 'minus' },
  };

  // Какая из двух форм ответа перед нами. Отдельной функцией — чтобы правило было
  // проверяемо тестом без DOM: ошибка здесь тихо уводит боевые подключения iiko на
  // чужой рендер, а в проде их три десятка. Отсутствующий `source` = ответ старого
  // бэкенда (окно деплой-скью) → прежняя ветка прав, как было до r_keeper.
  function rightsViewKind(data) {
    return data && data.source === 'capabilities' ? 'areas' : 'rights';
  }

  // Блок «Что даёт эта система» — области данных провайдера без сессии iiko.
  // Список приходит с бэкенда целиком (реестр integrations/data_areas.py), включая
  // неподдерживаемые: их и надо показать — ценность блока именно в честном «этого
  // не будет», а не в перечне того, что и так работает.
  function DataAreasList({ data, providerLabel = 'учётная система' }) {
    const areas = (data && data.areas) || [];
    if (!areas.length) return null;
    const missing = areas.filter((a) => a.status === 'unsupported');
    return (
      <div className="rights-check stack stack-8">
        <h4 className="t-h5" style={{ margin: 0 }}>Что даёт эта система</h4>
        <p className="t-caption text-mute" style={{ margin: 0 }}>
          Какие данные {providerLabel} отдаёт Ресториуму.
          {missing.length > 0 && (
            <>
              {' '}Со статусом «{AREA_STATUS.unsupported.label}» — этих данных
              в {providerLabel} нет: показатели, которые из них считаются, останутся
              в Аналитике пустыми. Так устроена сама система — это не настройка
              доступа, донастроить или перепроверить тут нечего.
            </>
          )}
        </p>
        <ul className="rights-check__list">
          {areas.map((a) => {
            const s = AREA_STATUS[a.status] || CAP_STATUS.unknown;
            return (
              <li key={a.key} className={'rights-check__item is-' + a.status}>
                <Icon name={s.icon} size={16} className="rights-check__ic" />
                <span className="rights-check__texts">
                  <span className="rights-check__label">{a.name}</span>
                  <span className="rights-check__right t-caption text-mute">{a.area}</span>
                </span>
                <Pill tone={s.tone} size="sm">{s.label}</Pill>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // data — ответ permissions-эндпоинта (или null); onRecheck — свежая проба (кнопка
  // рендерится только с колбэком), busy — её загрузка; providerLabel — «iiko»/«Syrve»/
  // «r_keeper» в текстах; recheckLabel — подпись кнопки (в онбординге — «Перепроверить»).
  function RightsChecklist({ data, onRecheck, busy = false, providerLabel = 'iiko',
                             recheckLabel = 'Проверить права' }) {
    // Развилка по source, а не по коду провайдера: правило «есть ли у системы права
    // API-пользователя» живёт на бэкенде в одном месте, фронт его не переизобретает.
    if (rightsViewKind(data) === 'areas') {
      return <DataAreasList data={data} providerLabel={providerLabel} />;
    }
    const caps = (data && data.capabilities) || [];
    if (!caps.length) return null;  // ещё не проверялось / нет подключения
    return (
      <div className="rights-check stack stack-8">
        <div className="cluster" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 className="t-h5" style={{ margin: 0 }}>Права API-пользователя</h4>
          {onRecheck && (
            <Button variant="ghost" size="sm" iconLeft="refresh" loading={busy} onClick={onRecheck}>
              {recheckLabel}
            </Button>
          )}
        </div>
        <p className="t-caption text-mute" style={{ margin: 0 }}>
          Что доступно API-пользователю {providerLabel} для аналитики. Недостающие права
          выдайте в {providerLabel} (Администрирование → Права доступа) и нажмите
          «{recheckLabel}».
        </p>
        <ul className="rights-check__list">
          {caps.map((c) => {
            const s = CAP_STATUS[c.status] || CAP_STATUS.unknown;
            return (
              <li key={c.key} className={'rights-check__item is-' + c.status}>
                <Icon name={s.icon} size={16} className="rights-check__ic" />
                <span className="rights-check__texts">
                  <span className="rights-check__label">
                    <span className="rights-tag">{c.tag}</span> {c.name}
                    {c.optional && <span className="t-caption text-mute"> · необязательно</span>}
                  </span>
                  <span className="rights-check__right t-caption text-mute">
                    {c.area}{c.also && c.also.length ? ' · также: ' + c.also.join(', ') : ''}
                  </span>
                </span>
                <Pill tone={s.tone} size="sm">{s.label}</Pill>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

export { RightsChecklist, DataAreasList, rightsViewKind, CAP_STATUS, AREA_STATUS };
